import { useState, useRef, useEffect } from "react";
import { useUser } from "../store";
import "./FacilityDashboard.scss";
import { notify } from "../util/notify";
import { toPrettyCurrency, toMonthYear, AgingReport } from "common";
import { useReports } from "../store/reports";
import { BreadCrumbs } from "./BreadCrumbs";
import _, { set } from "lodash";
import { StatusIcon } from "./StatusIcon";
import { SlugLink } from "./SlugLink";
import { ReportRow } from "./ReportRow";
import { AgingReportBalanceStatusType } from "../util/getAgingReportStatus";
import ReportMessages from "./ReportMessages";
import clsx from 'clsx';
import Modal from "./Modal";


export default function FacilityDashboard() {
  const { invokeApi, user, group, facility, payer } = useUser();
  const tableRef = useRef(null);
  const [activeReport, setActiveReport] = useState();
  const {
    reports,
    months,
    displayableReports,
    totals,
    setSearchTerm,
    statusFilters,
    setStatusFilters,
    updateReport,
  } = useReports();
  
  const [file, setFile] = useState();
  const [searchQuery, setSearchQuery] = useState("");
  const totalReportsOnLoad = reports.filter((report) => !report.archived).length;

  const uploadReport = async (e) => {
    e.preventDefault();
    if (file) {
      await invokeApi({
        endpoint: "/uploadReport",
        data: { file },
      }).then(() => {
        notify.success(
          "Successfully uploaded report - please check back shortly.",
          { duration: 5000 }
        );
        setFile(null);
      });
    }
  };

  const onFileSelected = (event) => {
    var file = event.target.files[0];
    const { name, type } = file;
    if (file) {
      var reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = function (evt) {
        setFile({ name, type, content: evt.target.result });
      };
    }
  };

  const StatusCheckbox = ({ label, status }) => {
    const isSelected = statusFilters.includes(status);

    return (
      <label className={`dashboard-status-filter is-${status}${isSelected ? " is-selected" : ""} checkbox`}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            if (e.target.checked) {
              setStatusFilters([...statusFilters, status]);
            } else {
              setStatusFilters(_.without(statusFilters, status));
            }
          }}
        />{" "}
        {label} <StatusIcon statusType={status} />
      </label>
    );
  };

  const statusFiltersBar = (
    <div className="status-key dashboard-status-key" aria-label="Filter reports by status">
      <span className="key-specifier">Status</span>
      <StatusCheckbox
        label="Paid"
        status={AgingReportBalanceStatusType.Paid}
      />
      <StatusCheckbox
        label="Changed"
        status={AgingReportBalanceStatusType.Changed}
      />
      <StatusCheckbox
        label="Outstanding"
        status={AgingReportBalanceStatusType.Outstanding}
      />
      <StatusCheckbox
        label="Deadline Passed"
        status={AgingReportBalanceStatusType.DeadlinePassed}
      />
      <StatusCheckbox
        label="Extended Deadline Passed"
        status={AgingReportBalanceStatusType.ExtendedDeadlinePassed}
      />
      <StatusCheckbox
        label="Message Provided"
        status={AgingReportBalanceStatusType.MessageProvided}
      />
      <StatusCheckbox
        label="Archived"
        status={AgingReportBalanceStatusType.Archived}
      />
    </div>
  );

  const onTableScroll = (e) => {
    localStorage.setItem(`${facility}#${payer}#scrollTop`, e.target.scrollTop);
  };

  useEffect(() => {
    if (tableRef.current)
      tableRef.current.scrollTop =
        localStorage.getItem(`${facility}#${payer}#scrollTop`) || 0;
  }, [displayableReports]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveReport(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  return (
    <>
      <Modal isActive={!!activeReport} onCancel={() => setActiveReport(null)} >      
        {activeReport && <ReportMessages report={activeReport} onCancel={() => setActiveReport(null)} />}
      </Modal>
      <div className="content facility-dashboard">
        <BreadCrumbs
          path={[group, facility, payer]}
          labels={["My Facilities", facility, payer]}
        />
        <div className="dashboard-heading">
          <div>
            <span className="dashboard-eyebrow">Aging report</span>
            <h1 className="title is-capitalized">
              {facility} <span aria-hidden="true">/</span> {payer}
            </h1>
          </div>
          {totalReportsOnLoad > 0 && (
            <div
              className="dashboard-report-count"
              aria-label={`${displayableReports.length} of ${totalReportsOnLoad} reports`}
            >
              <strong>{displayableReports.length}</strong>
              <span>of {totalReportsOnLoad}</span>
              <span>reports</span>
            </div>
          )}
        </div>

        <div className="actions dashboard-toolbar">
          <div className="field dashboard-search">
            <div className="control has-icons-left">
              <input
                className="input"
                type="text"
                placeholder="Search residents or bill types"
                aria-label="Search residents or bill types"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchTerm(e.target.value);
                }}
              />
              {searchQuery && (
                <button
                  className="dashboard-search-clear"
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setSearchQuery("");
                    setSearchTerm("");
                  }}
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              )}
              <span className="icon is-small is-left">
                <i className="fa-solid fa-magnifying-glass"></i>
              </span>
            </div>
          </div>

          {user.type === "Admin" && (
            <div className="field is-pulled-right has-addons">
              <div className="control">
                <label className="file-label ">
                  <input
                    className="file-input"
                    type="file"
                    onChange={onFileSelected}
                  />
                  <span className="file-name">
                    <i className="fas fa-upload"></i> &nbsp;
                    {file?.name || "No File Chosen"}
                  </span>
                </label>
              </div>
              <div className="control">
                <button className="button is-success" onClick={uploadReport}>
                  {" "}
                  Upload Report{" "}
                </button>
              </div>
              <div className="control">
                <SlugLink
                  path={[group, facility, payer, "backups"]}
                  className="button is-info"
                >
                  Backups
                </SlugLink>
              </div>
            </div>
          )}
        </div>
        <div className="dashboard-results">
          {!reports.length ? (
            <>
              {statusFiltersBar}
              <div className="dashboard-empty-state">
                <span className="dashboard-empty-icon" aria-hidden="true">
                  <i className="fa-solid fa-file-invoice-dollar" />
                </span>
                <h2>No aging reports yet</h2>
                <p>Upload an aging report to start tracking claims for this facility.</p>
              </div>
            </>
          ) : !displayableReports.length ? (
            <>
              {statusFiltersBar}
              <div className="dashboard-empty-state dashboard-no-results">
                <span className="dashboard-empty-icon" aria-hidden="true">
                  <i className="fa-solid fa-magnifying-glass" />
                </span>
                <h2>No matching reports</h2>
                <p>Try a different search or clear the active status filters.</p>
                <button
                  className="button is-light"
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setSearchTerm("");
                    setStatusFilters([]);
                  }}
                >
                  Clear search and filters
                </button>
              </div>
            </>
          ) : (
            <div
              className="table-container no-wrap dashboard-table-container"
              ref={tableRef}
              onScroll={onTableScroll}
            >
              {statusFiltersBar}
              <table className="is-size-7 is-clearfix table is-bordered is-striped is-narrow is-hoverable is-fullwidth has-text-centered">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Bill Type</th>
                    {months?.map((month) => (
                      <th key={month}>{toMonthYear(month)}</th>
                    ))}

                    <th>Prior</th>
                    <th>Net</th>
                    <th>Medicaid Application</th>
                    <th>Aging Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayableReports?.map((report) => (
                    <ReportRow
                      report={report}
                      statusFilters={statusFilters}
                      key={report.pk + report.sk}
                      months={months}
                      updateReport={updateReport}
                      setActiveReport={setActiveReport}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th>Total: {displayableReports.length}</th>
                    <th></th>

                    {months?.map((month) => (
                      <th key={month}>{toPrettyCurrency(totals[month])}</th>
                    ))}
                    <th>{toPrettyCurrency(toPrettyCurrency(totals.prior))}</th>
                    <th>{toPrettyCurrency(toPrettyCurrency(totals.net))}</th>
                    <th></th>
                    <th></th>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
